'use client';

import { useLocale, useTranslations } from 'next-intl';
import React, { useMemo } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

import Banner from '@/app/[locale]/(default-layout)/_components/Banner';

import { WEBAPP_URL } from '@/constants/http-constants';

import { addUtmParams } from '@/helpers/utm-helper';
import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { responsive, singleSlide } from '@cineamo/ui/src/helpers/carousel-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconNewRequest16 } from '@cineamo/ui/src/icons/ic-newRequest';
import {
    isBeforeKinofest,
    ImageKinofestBanner_1,
    ImageKinofestBannerTrailing_1_DE,
    ImageKinofestBannerTrailing_1_EN,
    kinofestCinemas
} from '@cineamo/ui/src/misc/kinofest/kinofest-constants';
import KinofestBannerItem, { KinofestBannerItemSize } from '@cineamo/ui/src/misc/kinofest/KinofestBannerItem';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const CinemaScreenImage = loadAsset('/images/placeholders/im-cinemaScreen.png');
const BusinessBackgroundImage = loadAsset('/images/banner-background/im-business.png');
const ChildsPartyBackgroundImage = loadAsset('/images/banner-background/im-childs-party.png');
const ClassicBackgroundImage = loadAsset('/images/banner-background/im-classic.png');
const DateNightBackgroundImage = loadAsset('/images/banner-background/im-date-night.png');
const GamingBackgroundImage = loadAsset('/images/banner-background/im-gaming.png');
const PartyGamesBackgroundImage = loadAsset('/images/banner-background/im-party-games.png');
const SchoolScreeningBackgroundImage = loadAsset('/images/banner-background/im-school-screening.png');

export type RentYourOwnCinemaCardContent = {
    text?: string;
    backgroundImage: string;
    backgroundColor?: string;
    trailingImage?: string;
    kinofestContent?: boolean;
};

type RentYourOwnCinemaCardProps = {
    className?: string;
    singleCustomString?: string;
    customButtonHref?: string;
    showKinofestItems?: boolean;
};

const RentYourOwnCinemaCard: React.FC<RentYourOwnCinemaCardProps> = ({
    className,
    singleCustomString,
    customButtonHref,
    showKinofestItems
}) => {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const locale = useLocale();
    const rentYourOwnCinemaSlides: RentYourOwnCinemaCardContent[] = useMemo(() => {
        return singleCustomString
            ? [{ text: singleCustomString, backgroundImage: CinemaScreenImage }]
            : [
                  {
                      text: t('banner.choose'),
                      backgroundImage: CinemaScreenImage
                  },
                  {
                      text: t('banner.gaming'),
                      backgroundImage: GamingBackgroundImage
                  },
                  {
                      text: t('banner.business'),
                      backgroundImage: BusinessBackgroundImage
                  },
                  {
                      text: t('banner.classic'),
                      backgroundImage: ClassicBackgroundImage
                  },
                  {
                      text: t('banner.child-birthday'),
                      backgroundImage: ChildsPartyBackgroundImage
                  },
                  {
                      text: t('banner.date-night'),
                      backgroundImage: DateNightBackgroundImage
                  },
                  {
                      text: t('banner.school'),
                      backgroundImage: SchoolScreeningBackgroundImage
                  },
                  {
                      text: t('banner.party-games'),
                      backgroundImage: PartyGamesBackgroundImage
                  }
              ];
    }, [singleCustomString, t]);

    const cinemaId = domainConfiguration?.cinema?.id;

    const rentYourOwnCinemaCardContentsArray: RentYourOwnCinemaCardContent[] = useMemo(() => {
        return rentYourOwnCinemaSlides.reduce<RentYourOwnCinemaCardContent[]>(
            (previousValue, currentValue, currentIndex) => {
                const isKinofestCinema = kinofestCinemas.includes(cinemaId);
                if (isBeforeKinofest && isKinofestCinema && showKinofestItems === true && !singleCustomString) {
                    const backgroundImage = ImageKinofestBanner_1;
                    // const backgroundImage = currentIndex % 2 ? ImageKinofestBanner_2 : ImageKinofestBanner_1;
                    const backgroundColor = currentIndex % 2 ? 'bg-[#221C16]' : 'bg-[#16232B]';
                    const trailingImage =
                        locale === 'de' ? ImageKinofestBannerTrailing_1_DE : ImageKinofestBannerTrailing_1_EN;
                    // const trailingImage =
                    //     currentIndex % 2 ? ImageKinofestBannerTrailing_2 : ImageKinofestBannerTrailing_1;
                    previousValue.push({
                        backgroundImage: backgroundImage,
                        backgroundColor: backgroundColor,
                        trailingImage: trailingImage,
                        kinofestContent: true
                    });
                }
                previousValue.push(currentValue);
                return previousValue;
            },
            []
        );
    }, [rentYourOwnCinemaSlides, showKinofestItems, singleCustomString, cinemaId, locale]);

    return (
        <Carousel
            className={cn(className, 'h-auto w-full')}
            responsive={responsive}
            arrows={false}
            autoPlay={singleSlide(rentYourOwnCinemaCardContentsArray)}
            autoPlaySpeed={8000}
            infinite={singleSlide(rentYourOwnCinemaCardContentsArray)}
            shouldResetAutoplay
            draggable={singleSlide(rentYourOwnCinemaCardContentsArray)}
            swipeable={singleSlide(rentYourOwnCinemaCardContentsArray)}>
            {rentYourOwnCinemaCardContentsArray.map((item, index) => {
                if (item.kinofestContent) {
                    return (
                        <KinofestBannerItem
                            key={`KinofestBannerItem#${index}`}
                            backgroundImage={item.backgroundImage}
                            classNameBackground={item.backgroundColor}
                            trailingImage={item.trailingImage ?? ''}
                            size={KinofestBannerItemSize.thin}
                        />
                    );
                }
                return (
                    <Banner
                        key={`carouselItem${index}`}
                        classNameImage={cn(item.kinofestContent ? 'w-full lg:w-2/3 xl:w-1/2' : 'w-full')}
                        backgroundImage={item.backgroundImage}
                        href={
                            customButtonHref ||
                            addUtmParams(`${WEBAPP_URL}/${locale}/program/request`, {
                                utmSource: 'cinema_website',
                                utmContent: 'rent_cinema_card',
                                utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                                utmMedium: 'website'
                            })
                        }
                        target="_blank"
                        text={item.text}
                        buttonLeadingIcon={<IconNewRequest16 />}
                        buttonTitle={t('banner.book-cinema')}
                    />
                );
            })}
        </Carousel>
    );
};

export default RentYourOwnCinemaCard;
